import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { toast } from 'react-hot-toast';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { getServicesAndPricingApi } from '../../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn } from '../../../utils/commonUtil';

// Components
import { unitPriceSuffix } from '../../../components/Utils';
import { Section, Placeholder, LoadingRow } from './ui';
import { SECTION_IDS, isOk } from './helpers';

const tenant = import.meta.env.VITE_TENANT_NAME;
const defaultQty = (serviceId) => (tenant === 'pushlaundry' && serviceId === 13 ? 18 : 1);

// Quantity stepper that lets the user clear the field while typing without dropping the service.
function QtyStepper({ value, onChange }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (Number.isInteger(n) && n >= 0) onChange(n);
    else setDraft(String(value));
  };

  return (
    <div className='flex items-center rounded-md border border-slate-200 bg-white' onClick={(e) => e.stopPropagation()}>
      <button
        type='button'
        aria-label='Decrease quantity'
        className='flex h-8 w-8 items-center justify-center rounded-l-md text-slate-500 hover:bg-slate-50'
        onClick={() => onChange(value - 1)}
      >
        <MinusIcon className='h-3.5 w-3.5' />
      </button>
      <input
        type='text'
        inputMode='numeric'
        aria-label='Quantity'
        className='h-8 w-10 border-0 p-0 text-center text-sm font-semibold text-slate-900 focus:ring-0'
        value={draft}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '');
          setDraft(v);
          if (v !== '' && +v > 0) onChange(+v);
        }}
        onBlur={(e) => commit(e.target.value)}
      />
      <button
        type='button'
        aria-label='Increase quantity'
        className='flex h-8 w-8 items-center justify-center rounded-r-md text-slate-500 hover:bg-slate-50'
        onClick={() => onChange(value + 1)}
      >
        <PlusIcon className='h-3.5 w-3.5' />
      </button>
    </div>
  );
}

export default function ServicesSection() {
  const dispatch = useDispatch();
  const { orderServiceAddress, selectedServices } = useSelector((s) => s.createNewOrder);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Services & pricing are per address.
  useEffect(() => {
    if (!orderServiceAddress?.id) return;
    let stale = false;
    setLoading(true);
    getServicesAndPricingApi(orderServiceAddress.id).then((res) => {
      if (stale) return;
      setLoading(false);
      if (isOk(res)) {
        const list = (res?.data || []).map((s) => ({
          id: s.id,
          service: s.name,
          unit: s.unit_type,
          client_fee: s.client_fee,
          priority_fee: s.priority_fee,
          image: s.image?.absolute_url || null,
        }));
        setServices(list);
        dispatch(setOrderDataAction({ availableServices: list }));
        if (list.length && isEmpty(selectedServices))
          dispatch(
            setOrderDataAction({ selectedServices: [{ service_id: list[0].id, quantity: defaultQty(list[0].id) }] })
          );
      } else {
        setServices([]);
        toast.error(res?.message || 'Something went wrong');
      }
    });
    return () => {
      stale = true;
    };
  }, [orderServiceAddress?.id]);

  const qtyOf = (id) => selectedServices?.find((s) => s.service_id === id)?.quantity || 0;
  const setQty = (id, qty) => {
    const rest = (selectedServices || []).filter((s) => s.service_id !== id);
    if (qty <= 0) dispatch(setOrderDataAction({ selectedServices: rest }));
    else if (qtyOf(id)) {
      dispatch(
        setOrderDataAction({
          selectedServices: selectedServices.map((s) => (s.service_id === id ? { ...s, quantity: qty } : s)),
        })
      );
    } else
      dispatch(
        setOrderDataAction({ selectedServices: [...(selectedServices || []), { service_id: id, quantity: qty }] })
      );
  };

  const count = selectedServices?.length || 0;
  const disabled = !orderServiceAddress;

  return (
    <Section
      id={SECTION_IDS.services}
      step={2}
      title='Services'
      subtitle={count ? `${count} selected` : null}
      summary={
        (selectedServices || [])
          .map((s) => {
            const svc = services.find((x) => x.id === s.service_id);
            return svc ? `${svc.service} × ${s.quantity}` : null;
          })
          .filter(Boolean)
          .join(', ') || null
      }
      done={count > 0}
      disabled={disabled}
    >
      {disabled ? (
        <Placeholder>Choose a pickup address to see services and pricing.</Placeholder>
      ) : loading ? (
        <LoadingRow text='Loading services…' />
      ) : services.length === 0 ? (
        <Placeholder>No services are available at this address.</Placeholder>
      ) : (
        <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
          {services.map((svc) => {
            const qty = qtyOf(svc.id);
            const selected = qty > 0;
            return (
              <div
                key={svc.id}
                className={`flex items-center gap-3 rounded-md border p-2.5 transition ${
                  selected ? 'border-client-300 bg-client-300/5 ring-client-300 ring-1' : 'border-slate-200'
                }`}
              >
                {svc.image ? (
                  <img
                    src={svc.image}
                    alt=''
                    className='h-10 w-10 shrink-0 rounded-md bg-slate-50 object-contain p-1'
                  />
                ) : (
                  <div className='h-10 w-10 shrink-0 rounded-md bg-slate-100' />
                )}
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-semibold text-slate-900'>{svc.service}</p>
                  <p className='text-xs text-slate-500'>
                    {amountCheckFn(svc.client_fee)}/{unitPriceSuffix(svc.unit)}
                  </p>
                </div>
                {selected ? (
                  <QtyStepper value={qty} onChange={(n) => setQty(svc.id, n)} />
                ) : (
                  <button
                    type='button'
                    onClick={() => setQty(svc.id, defaultQty(svc.id))}
                    className='hover:border-client-300 hover:text-client-300 flex h-8 items-center gap-1 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition'
                  >
                    <PlusIcon className='h-3.5 w-3.5' />
                    Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
